import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSelect from "components/GKSelect";
import GKTextField from "components/GKTextField";
import { useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import {
  COMPANY_CAP,
  COMPANY_INSTTYPE,
  COMPANY_STATUS,
  INSTRUMENT_SUB_TYPE,
  MUTUAL_FUND_SUB_TYPE,
} from "utils/constants";

function CreateCompanyModal(props) {
  const { isModalOpen, setIsModalOpen, refetch } = props;

  const { mutate, isLoading } = useMutation({
    mutationKey: ["CREATE_COMPANY"],
    mutationFn: (data) => axiosInstance.post(`/admin-instrument/create/`, data),
    onSuccess: () => {
      refetch();
      setIsModalOpen(false);
    },
    onError: () => {},
  });

  const { handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      cap: "",
      finCode: "",
      house: "",
      industry: "",
      instType: "",
      kiteCode: "",
      name: "",
      sector: "",
      shortName: "",
      status: "",
      bseCode: "",
      nseCode: "",
      isinCode: "",
      series: "",
      instSubType: "",
      amfiCode: "",
      mfSubType: "",
    },
  });

  const onSubmit = (values) => {
    mutate(values);
  };

  return (
    <GKModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      modalTitle={"Add New Company"}
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            variant="outlined"
            onClick={() => setIsModalOpen(false)}
          >
            Close
          </Button>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            type="submit"
            form="create-company-form"
            variant="outlined"
          >
            {isLoading ? <CircularProgress size={14} /> : "Submit"}
          </Button>
        </Grid>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} id="create-company-form">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="name"
                onChange={(e) => setValue("name", e.target.value)}
                value={watch("name") || ""}
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="ShortName"
                onChange={(e) => setValue("shortName", e.target.value)}
                value={watch("shortName") || ""}
                name="shortName"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Fin Code"
                onChange={(e) => setValue("finCode", e.target.value)}
                value={watch("finCode") || ""}
                name="finCode"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="InstType"
                onChange={(e) => setValue("instType", e.target.value)}
                value={watch("instType") || ""}
                name="instType"
              >
                {COMPANY_INSTTYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Cap"
                onChange={(e) => setValue("cap", e.target.value)}
                value={watch("cap") || ""}
                name="cap"
              >
                {COMPANY_CAP.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="BSE"
                onChange={(e) => setValue("bseCode", e.target.value)}
                value={watch("bseCode") || ""}
                name="bseCode"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="NSE"
                onChange={(e) => setValue("nseCode", e.target.value)}
                value={watch("nseCode") || ""}
                name="nseCode"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Mutual Funds Sub Type"
                onChange={(e) => setValue("mfSubType", e.target.value)}
                value={watch("mfSubType") || ""}
                name="mfSubType"
              >
                {MUTUAL_FUND_SUB_TYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="AMFI Code"
                onChange={(e) => setValue("amfiCode", e.target.value)}
                value={watch("amfiCode") || ""}
                name="amfiCode"
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Sector"
                onChange={(e) => setValue("sector", e.target.value)}
                value={watch("sector") || ""}
                name="sector"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Industry"
                onChange={(e) => setValue("industry", e.target.value)}
                value={watch("industry") || ""}
                name="industry"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="House"
                onChange={(e) => setValue("house", e.target.value)}
                value={watch("house") || ""}
                name="house"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Kite Code"
                onChange={(e) => setValue("kiteCode", e.target.value)}
                value={watch("kiteCode") || ""}
                name="kiteCode"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Status"
                onChange={(e) => setValue("status", e.target.value)}
                value={watch("status") || ""}
                name="status"
              >
                {COMPANY_STATUS.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="ISIN"
                onChange={(e) => setValue("isinCode", e.target.value)}
                value={watch("isinCode") || ""}
                name="isinCode"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Series"
                onChange={(e) => setValue("series", e.target.value)}
                value={watch("series") || ""}
                name="series"
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Instrument Sub Type"
                onChange={(e) => setValue("instSubType", e.target.value)}
                value={watch("instSubType") || ""}
                name="instSubType"
              >
                {INSTRUMENT_SUB_TYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
}

export default CreateCompanyModal;
